import { type FC, useCallback, useState } from 'react';

import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';

import type { AssetsSegmentElasticSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import type { MlPersonByAssetAndVersionNullable } from '@iconik-io/iconik-api/src/services/ml';

import Ellipsis from '../../../components/ellipsis/ellipsis';
import TriggerContextMenu from '../../../components/trigger_context_menu/trigger_context_menu';
import UserBubble from '../../../components/user_bubble/user_bubble';
import { selectIsTimeBasedAsset } from '../../../ducks/pages/asset/selectors';
import { setActivePerson } from '../../../ducks/panels/asset_face_recognition/slice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import IconButton from '../../../ui/icon_button/icon_button';
import type { MenuItem } from '../../../ui/menus/menu/types';
import RecognizedFaceContextMenu from './context_menu';
import { useScrollIntoView } from './hooks/use_scroll_into_view';
import PersonSelect from './person_select';
import { ToggleSegmentButton } from './segment_button';
import SegmentsList from './segments_list';

import styles from './recognized_persons.module.css';

type Props = {
  person: MlPersonByAssetAndVersionNullable;
  segments: AssetsSegmentElasticSchemaWithIdNullable[];
  highlighted: boolean;
  onPersonRowClick: (personId: string) => void;
  setSelectableItemIds?: (ids: string[]) => void;
};

const ConfirmedPerson: FC<Props> = ({
  person,
  segments = [],
  highlighted,
  onPersonRowClick,
  setSelectableItemIds,
}: Props) => {
  const dispatch = useAppDispatch();
  const [isSegmentsExpanded, setIsSegmentsExpanded] = useState(false);
  const [showNewPersonSelect, setShowNewPersonSelect] = useState(false);
  const { id: assetId, versionId } = useParams({ strict: false });
  const confirmedPersonRowRef = useScrollIntoView(highlighted);

  const isTimeBasedAsset = useAppSelector((state) =>
    selectIsTimeBasedAsset(state, assetId, versionId),
  );
  const shouldShowSegments = isTimeBasedAsset && segments.length > 0;
  const isSegmentsListVisible =
    isSegmentsExpanded && shouldShowSegments && !showNewPersonSelect;

  const highlightedSegmentIds = useAppSelector(
    (state) => state.segments.highlightedSegmentIds,
  );

  const handleMenuItemClick = useCallback((option: MenuItem) => {
    if (option.id === 'change_person') {
      setShowNewPersonSelect(true);
    }
  }, []);

  return (
    <div
      data-id={person.id}
      ref={confirmedPersonRowRef}
      className={styles.person_item}
      aria-label="Recognized Person - Confirmed"
    >
      <div
        className={classNames(styles.person_row, {
          [styles.highlighted]: highlighted,
        })}
        onClick={() => onPersonRowClick(person.id)}
      >
        <div className={styles.person_row_inner}>
          <UserBubble
            className={styles.person_avatar}
            size="md"
            variant="round"
            name={person.name ?? ''}
            src={person.image_url ?? undefined}
          />
          <div className={styles.confirmed_person_name_container}>
            <div className={styles.name}>
              <Ellipsis>
                {person.name ? (
                  person.name
                ) : (
                  <span className={styles.unknown_person_name}>
                    Unnamed person
                  </span>
                )}
              </Ellipsis>
            </div>

            <div
              className={styles.person_row_inner}
              onClick={(event) => {
                event.stopPropagation();
                dispatch(setActivePerson(undefined));
              }}
            >
              {shouldShowSegments ? (
                <ToggleSegmentButton
                  isExpanded={isSegmentsExpanded}
                  onChange={setIsSegmentsExpanded}
                >
                  {segments.length}
                </ToggleSegmentButton>
              ) : null}
            </div>
          </div>
        </div>
        <div
          onClick={(event) => {
            event.stopPropagation();
            dispatch(setActivePerson(undefined));
          }}
        >
          <TriggerContextMenu
            triggerElement={
              <IconButton
                onClick={() => {
                  setSelectableItemIds?.([person.id]);
                }}
                iconName="menu_context"
                aria-label="Open person actions menu"
              />
            }
            contextMenu={RecognizedFaceContextMenu}
            contextItems={[{ ...person }]}
            onMenuItemClick={handleMenuItemClick}
          />
        </div>
      </div>
      {isSegmentsListVisible ? (
        <SegmentsList
          highlightedSegmentIds={highlightedSegmentIds}
          person={person}
          segments={segments}
        />
      ) : null}
      {showNewPersonSelect ? (
        <PersonSelect
          segments={segments}
          onSetShowPersonSelect={setShowNewPersonSelect}
          onDeactivate={() => {
            dispatch(setActivePerson(undefined));
            setSelectableItemIds?.([]);
          }}
          person={person}
          actionType="change"
          rowType="confirmed"
        />
      ) : null}
    </div>
  );
};

export default ConfirmedPerson;
